/**
 * TanStack Query bound to the office selector. The query key gets the
 * selector value appended, and the request carries the same value in the
 * `x-velora-office` header, so each office (and "All offices") has its own
 * cache entry and switching office refetches every scoped screen.
 *
 * Live updates: start the key with the entity key (`['appointment', ...]`)
 * and the shell refetches it when that entity changes (see useLive).
 *
 * `placeholderData` (keep the previous result while a new key loads) only
 * applies within one office scope: after an office switch the screen shows
 * its loading state, never the previous office's numbers under the new
 * office's name (the All offices huddle once read "Velora Demo Midland ...").
 */
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import type { HttpClient } from '@velora/sdk';
import { clientFor } from '../api.js';
import { useOfficeScope } from './OfficeScope.js';

export interface OfficeQueryOptions<T> extends Omit<
  UseQueryOptions<T, Error, T, readonly unknown[]>,
  'queryKey' | 'queryFn'
> {
  queryKey: readonly unknown[];
  queryFn: (client: HttpClient, signal: AbortSignal) => Promise<T>;
}

export function officeQueryKey(queryKey: readonly unknown[], office: string): readonly unknown[] {
  return [...queryKey, { office }];
}

/** The office param a key built by `officeQueryKey` was made for. */
function officeOfKey(key: readonly unknown[] | undefined): string | undefined {
  const last = key?.[key.length - 1];
  return last && typeof last === 'object' && 'office' in last
    ? String((last as { office: unknown }).office)
    : undefined;
}

/**
 * Wraps a `placeholderData` function so it answers only for a previous query
 * of the same office scope. TanStack's placeholder types are not expressible
 * generically here; the wrapper only forwards to the caller's function.
 */
export function scopedPlaceholder<P>(placeholderData: P, param: string): P {
  if (typeof placeholderData !== 'function') return placeholderData;
  const forward = placeholderData as (prev: unknown, prevQuery: unknown) => unknown;
  return ((prev: unknown, prevQuery?: { queryKey: readonly unknown[] }) =>
    prevQuery && officeOfKey(prevQuery.queryKey) !== param
      ? undefined
      : forward(prev, prevQuery)) as unknown as P;
}

export function useOfficeQuery<T>(options: OfficeQueryOptions<T>): UseQueryResult<T, Error> {
  const { param, locationIds } = useOfficeScope();
  const { queryKey, queryFn, enabled = true, placeholderData, ...rest } = options;
  const placeholder = scopedPlaceholder(placeholderData, param);
  return useQuery({
    ...rest,
    ...(placeholder !== undefined ? { placeholderData: placeholder } : {}),
    queryKey: officeQueryKey(queryKey, param),
    queryFn: ({ signal }) => queryFn(clientFor(param), signal),
    enabled: enabled !== false && locationIds.length > 0,
  });
}

/**
 * The office param for reading ONE patient's clinical record (images, perio).
 *
 * The patient row is shared across the practice's offices, but clinical rows
 * belong to the office that made them. Under a single selected office a
 * patient from another office opened with no X-rays and no perio, with
 * nothing saying why (W3, 2026-10-28: "no one's going to use this"). These
 * reads therefore ask for every office the user holds; the API still narrows
 * each request to the offices where the user has the permission and the
 * module. Writes and captures keep the selected office.
 */
export const PATIENT_CLINICAL_READ_PARAM = 'all';

export function usePatientClinicalParam(): string {
  const { offices, param } = useOfficeScope();
  return offices.length > 1 ? PATIENT_CLINICAL_READ_PARAM : param;
}

/** `useOfficeQuery` for one patient's clinical record, read across the user's offices. */
export function usePatientClinicalQuery<T>(
  options: OfficeQueryOptions<T>,
): UseQueryResult<T, Error> {
  const { locationIds } = useOfficeScope();
  const param = usePatientClinicalParam();
  const { queryKey, queryFn, enabled = true, placeholderData, ...rest } = options;
  const placeholder = scopedPlaceholder(placeholderData, param);
  return useQuery({
    ...rest,
    ...(placeholder !== undefined ? { placeholderData: placeholder } : {}),
    queryKey: officeQueryKey(queryKey, param),
    queryFn: ({ signal }) => queryFn(clientFor(param), signal),
    enabled: enabled !== false && locationIds.length > 0,
  });
}
